/**
 * E-mail pelo EmailEngine (migração Nylas → EmailEngine): as chamadas às funções `mail-*`.
 *
 * Em produção a escolha é por caixa (`usesEmailEngine*`, abaixo). Em `npm run dev`,
 * `VITE_EMAIL_SOURCE=emailengine` força todas as caixas pelo EmailEngine — pré-visualização das
 * contas ainda em modo sombra (`mail_provider = 'nylas'`), por isso o pedido leva
 * `provider: 'emailengine'`; caixa já virada não depende disso.
 */
import { supabase } from '@/integrations/supabase/client';
import type { Email, EmailFolder } from '@/types/email';

export const isEmailEngineSource = (): boolean =>
  import.meta.env.DEV && import.meta.env.VITE_EMAIL_SOURCE === 'emailengine';

/**
 * Provedor por caixa (produção): caixa com `mail_provider = 'emailengine'` usa as funções `mail-*`;
 * as demais seguem nas `nylas-*`. Em dev, `VITE_EMAIL_SOURCE=emailengine` força o EmailEngine para
 * todas, como antes.
 *
 * O useAuthManager registra as caixas da org ao carregar; telas que não carregam a lista (perfil de
 * lead, negócio…) caem na consulta de `usesEmailEngineFor`, que lê só a coluna e guarda o resultado.
 */
const providerByAccount = new Map<string, string>();

export function registerMailProviders(accounts: Array<{ id: string; mail_provider?: string | null }>): void {
  for (const a of accounts) providerByAccount.set(a.id, a.mail_provider || 'nylas');
}

/** Síncrono, só com o que já foi registrado — para decisões de tela. */
export function usesEmailEngine(accountId?: string | null): boolean {
  if (isEmailEngineSource()) return true;
  return !!accountId && providerByAccount.get(accountId) === 'emailengine';
}

/** Para chamadas: consulta o banco quando a caixa ainda não foi registrada. */
export async function usesEmailEngineFor(accountId?: string | null): Promise<boolean> {
  if (isEmailEngineSource()) return true;
  if (!accountId) return false;
  if (!providerByAccount.has(accountId)) {
    const { data } = await supabase.from('email_accounts').select('id, mail_provider').eq('id', accountId).maybeSingle();
    if (data) registerMailProviders([data]);
  }
  return providerByAccount.get(accountId) === 'emailengine';
}

/** Caixas novas (OAuth e IMAP) nascem no EmailEngine — decisão do Gabriel em 26/10/2026. */
export const newConnectionsUseEmailEngine = (): boolean => true;

/** `functions.invoke` esconde o corpo do erro; a mensagem útil vem no `context` da resposta. */
async function invokeError(error: unknown): Promise<Error> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    const body = await ctx.json().catch(() => null);
    if (body?.error) return new Error(String(body.error));
  }
  return error instanceof Error ? error : new Error('Falha ao ler o EmailEngine');
}

/**
 * Uma chamada por vez por caixa. O EmailEngine não aguenta comandos simultâneos na mesma caixa: no
 * Gmail por OAuth, três listagens em paralelo deram 502 ("OAuth2 request failed" / "Timeout waiting
 * for command response") enquanto as mesmas três em sequência passaram (26/10/2026).
 */
const accountQueue = new Map<string, Promise<unknown>>();

export function serialByAccount<T>(accountId: unknown, fn: () => Promise<T>): Promise<T> {
  if (typeof accountId !== 'string' || !accountId) return fn();
  const run = (accountQueue.get(accountId) ?? Promise.resolve()).catch(() => undefined).then(fn);
  accountQueue.set(accountId, run.catch(() => undefined));
  return run;
}

/**
 * Chama uma função `mail-*` com `provider: 'emailengine'` e devolve o corpo ou a mensagem de erro
 * legível (a recusa da trava de pré-visualização, por exemplo). Nunca lança.
 */
export async function eeCall<T = Record<string, unknown>>(
  fn: string,
  body: Record<string, unknown>,
  /**
   * keepBody: não acrescentar `provider: 'emailengine'`. Necessário na mail-oauth-start, onde
   * `provider` é o provedor do OAuth ('outlook') — sobrescrever dava "só o Outlook usa OAuth".
   */
  opts: { keepBody?: boolean } = {},
): Promise<{ data: T | null; error: string | null }> {
  const { data, error } = await serialByAccount(body.account_id, () =>
    supabase.functions.invoke(fn, { body: opts.keepBody ? body : { ...body, provider: 'emailengine' } }));
  if (error) return { data: null, error: (await invokeError(error)).message };
  if (data && typeof data === 'object' && 'error' in data && (data as { success?: boolean }).success !== true) {
    return { data: data as T, error: String((data as { error: unknown }).error) };
  }
  return { data: data as T, error: null };
}

/** Baixa um anexo pela mail-get-attachment e dispara o download no navegador. */
export async function eeDownloadAttachment(
  orgId: string,
  accountId: string,
  attachmentId: string,
  filename: string,
  contentType?: string,
): Promise<string | null> {
  const { data, error } = await serialByAccount(accountId, () => supabase.functions.invoke('mail-get-attachment', {
    body: { org_id: orgId, account_id: accountId, attachment_id: attachmentId, provider: 'emailengine' },
  }));
  if (error) return (await invokeError(error)).message;
  // A função responde octet-stream (senão o supabase-js lê PDF como texto); o tipo vem da lista.
  if (!(data instanceof Blob)) return 'O anexo não chegou como arquivo.';
  const blob = contentType ? new Blob([data], { type: contentType }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'anexo';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return null;
}

/** Listagem igual já em andamento é reaproveitada (a página dispara a mesma busca mais de uma vez). */
const listInFlight = new Map<string, Promise<{ emails: Email[]; nextCursor: string | null }>>();

export function eeListEmails(params: Parameters<typeof listOnce>[0]): Promise<{ emails: Email[]; nextCursor: string | null }> {
  const key = JSON.stringify(params);
  const pending = listInFlight.get(key);
  if (pending) return pending;
  // Uma nova tentativa: nos primeiros minutos depois de conectar, o EmailEngine ainda sincroniza e
  // às vezes não responde a tempo.
  const run = serialByAccount(params.accountId, () => listOnce(params))
    .catch(() => new Promise((r) => setTimeout(r, 1500)).then(() => serialByAccount(params.accountId, () => listOnce(params))))
    .finally(() => listInFlight.delete(key));
  listInFlight.set(key, run);
  return run;
}

async function listOnce(params: {
  orgId: string;
  accountId?: string;
  folder: EmailFolder;
  cursor?: string | null;
  limit: number;
  search?: string;
  unread?: boolean | null;
}): Promise<{ emails: Email[]; nextCursor: string | null }> {
  if (!params.accountId) throw new Error('Selecione uma caixa para ler pelo EmailEngine.');
  const { data, error } = await supabase.functions.invoke('mail-list-emails', {
    body: {
      org_id: params.orgId,
      account_id: params.accountId,
      folder: params.folder,
      limit: params.limit,
      page_token: params.cursor ?? null,
      search: params.search || '',
      unread: params.unread === true,
      provider: 'emailengine',
    },
  });
  if (error) throw await invokeError(error);
  const list = (data?.data ?? []) as Array<Record<string, unknown>>;
  return {
    emails: list.map((raw) => ({ ...(raw as unknown as Email), date: new Date(String(raw.date)) })),
    nextCursor: (data?.next_cursor as string | null) ?? null,
  };
}

export async function eeGetEmailBody(orgId: string, accountId: string, messageId: string): Promise<string> {
  const { data, error } = await serialByAccount(accountId, () => supabase.functions.invoke('mail-get-email-body', {
    body: { org_id: orgId, account_id: accountId, message_id: messageId, provider: 'emailengine' },
  }));
  if (error) throw await invokeError(error);
  return typeof data?.body === 'string' && data.body.trim()
    ? data.body
    : '<p class="text-muted-foreground">Sem conteúdo.</p>';
}
